import { Box, Menu, MenuItem, Stack } from '@mui/material'
import { Permissions } from 'c1gen/permissions'
import { C1Button, C1IconButton } from 'c1ui'
import { C1Icon } from 'components/core/icons/C1Icon'
import { usePermissionChecker } from 'components/no-ui/capability'
import { ChevronDown, MoreHorizontal } from 'lucide-react'
import React, { MouseEvent, useId, useRef, useState } from 'react'
import { useIntl } from 'react-intl'

/**
 * Represents a single menu option for C1MoreButton.
 */
export type C1MoreButtonOptionProps = {
    /** Stable identifier used as the React key when rendering MenuItems. Falls back to array index if omitted. */
    key?: string,
    /** The permission required to show this item. Options the user lacks permission for are hidden automatically. */
    permission: Permissions,
    /** Text or element displayed in the menu item. */
    label: React.ReactNode,
    /** Callback fired when this menu item is clicked. The menu closes automatically before invoking. */
    onClick: () => void,
    /** Greys out the menu item and prevents clicks while still showing it. */
    disabled?: boolean,
    /** Optional icon rendered to the left of the label. */
    icon?: React.ReactElement,
}

export type C1MoreButtonProps = {
    /** Array of menu options. Each option is permission-gated — only visible options are rendered. If none are visible, the entire button is hidden. */
    options: C1MoreButtonOptionProps[]
    /** When provided, renders a labeled C1Button with a chevron-down end icon instead of the default icon button. Use for primary actions with secondary alternatives (e.g. "Add source"). */
    label?: React.ReactNode
    /** Button variant. In labeled mode, passed directly to C1Button. In icon-only mode, `'outlined'` renders as `outlined` + `info` color and `'text'` renders as `text`. `'contained'` is not supported in icon-only mode and falls back to `'outlined'`. */
    variant?: 'outlined' | 'text' | 'contained'
    /** Optional test ID for the button element. */
    'data-testid'?: string
}

/**
 * Overflow action menu that shows permission-gated options in a dropdown.
 *
 * Wraps C1Button / C1IconButton with a MUI Menu popover. Options that the
 * current user lacks permission for are filtered out automatically; if no
 * options remain, the button renders nothing (`null`).
 *
 * ## Modes
 * - **Icon button** (default) — renders a `⋯` (MoreHorizontal) icon button.
 *   Use as the trailing action in table rows, detail page headers, or any
 *   context where space is limited.
 * - **Labeled button** — pass `label` to render a full C1Button with a
 *   chevron-down icon. Use when the menu represents a primary action with
 *   multiple sub-choices (e.g. "Add source", "Import app data").
 *
 * ## Variant mapping
 * - `outlined` (default) — icon mode renders `outlined` + `info` color;
 *   labeled mode renders `outlined`
 * - `text` — both modes render `text` variant
 *
 * ## Behavior
 * - The menu auto-focuses the first item on open for keyboard navigation
 * - Each option requires a `permission` value — the component calls
 *   `usePermissionChecker` to filter options before rendering
 * - Returns `null` when all options are filtered out (no empty button shell)
 * - Options can include an `icon` element rendered inline with the label
 *
 * @designSystem
 * @category atoms
 * @relatedComponent C1Button | Use for a single action without a dropdown menu
 * @relatedComponent C1IconButton | Use for a single icon-only action without a menu
 * @externalReference MUI Menu API | https://mui.com/material-ui/api/menu/
 * @externalReference MUI MenuItem API | https://mui.com/material-ui/api/menu-item/
 */
export const C1MoreButton: React.FC<C1MoreButtonProps> = ({
    options,
    label,
    variant = 'outlined',
    'data-testid': dataTestId,
}) => {
    const intl = useIntl()
    const id = useId()
    const buttonId = `c1-more-button-${id}`
    const menuId = `c1-more-menu-${id}`

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
    const open = Boolean(anchorEl)
    const hasPermission = usePermissionChecker()
    const menuRef = useRef<HTMLDivElement>(null)

    const handleClick = (event: MouseEvent<HTMLButtonElement>): void => {
        setAnchorEl(event.currentTarget)
    }

    const handleClose = (): void => {
        setAnchorEl(null)
    }

    // Filter options based on permissions before rendering
    const visibleOptions = options.filter((o) => o && hasPermission(o.permission))

    if (visibleOptions.length === 0) {
        return null
    }

    return <>
        {label
            ? <Box>
                <C1Button
                    id={buttonId}
                    data-testid={dataTestId}
                    variant={variant}
                    aria-controls={open ? menuId : undefined}
                    aria-haspopup="true"
                    aria-expanded={open}
                    onClick={handleClick}
                    endIcon={<C1Icon icon={ChevronDown} />}
                >
                    {label}
                </C1Button>
            </Box>
            : <C1IconButton
                id={buttonId}
                data-testid={dataTestId}
                aria-controls={open ? menuId : undefined}
                aria-haspopup="true"
                aria-expanded={open}
                aria-label={intl.formatMessage({ id: 'component_more_button_label', defaultMessage: 'More' })}
                onClick={handleClick}
                {...(variant === 'text'
                    ? { variant: 'text' as const }
                    : { variant: 'outlined' as const, color: 'info' })}
            >
                <C1Icon icon={MoreHorizontal} />
            </C1IconButton>
        }
        <Menu
            ref={menuRef}
            id={menuId}
            aria-label={intl.formatMessage({ id: 'component_more_button_menu', defaultMessage: 'Actions menu' })}
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            MenuListProps={{
                'aria-labelledby': buttonId,
                autoFocusItem: true,
                variant: 'menu',
            }}
            TransitionProps={{
                onEntered: (): void => {
                    // Focus first menu item after transition completes
                    // Use ref to scope query to this specific menu instance
                    if (menuRef.current) {
                        setTimeout(() => {
                            const firstMenuItem = menuRef.current?.querySelector('[role="menuitem"]') as HTMLElement
                            firstMenuItem?.focus()
                        }, 100)
                    }
                },
            }}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
            }}
            transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
            }}
        >
            {visibleOptions.map((o, i) => (
                <MenuItem
                    disabled={o.disabled}
                    key={o.key || i}
                    onClick={(): void => {
                        handleClose()
                        o.onClick()
                    }}
                >
                    {o.icon ? <Stack direction="row" alignItems="center" spacing={1}>
                        {o.icon}
                        <Box>{o.label}</Box>
                    </Stack> : o.label}
                </MenuItem>
            ))}
        </Menu>
    </>
}
